import type { ReactNode } from 'react';
import { Skeleton } from '@monksflow/monks-ui';
import { getWidgetStores } from '@/entities/widget';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PersonaListBlock } from '../PersonaListBlock';
import type { PersonaListProps } from './PersonaList.types';
import { useTranslation } from 'react-i18next';

export const PersonaList = ({
  personas,
  onPersonaClick,
  onStartConversation,
  isLoading,
  useCurrentAgentId,
  audienceFlowPersonaPrefix,
  isStartConversationDisabled = false,
  readOnly,
}: PersonaListProps) => {
  const { t } = useTranslation('audiences');

  // UI store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`. Production wiring at
  // app boot supplies the real `@/app/store/ui` module; tests stub
  // `getWidgetStores()` at the seam. Loose narrowing because the binding
  // shape carries `[key: string]: unknown` for the side-panel slice.
  const { uiStore } = getWidgetStores();
  const sidePanelView = uiStore.useSidePanel() as unknown as { sidePanelLayoutMode?: string };
  const isFullscreen = sidePanelView.sidePanelLayoutMode === 'fullscreen';
  const currentAgentId = useCurrentAgentId();

  // Use more columns in fullscreen mode since we have more space
  const gridClasses = isFullscreen
    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
    : 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4';

  // single call site, pure branch dispatch — inlined as a plain computed
  // value rather than a `renderContent`-style closure.
  let content: ReactNode;
  if (isLoading) {
    content = (
      <div role="status" aria-busy="true" className={gridClasses}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} data-testid="persona-list-skeleton-card" className="h-45 rounded-3xl" />
        ))}
      </div>
    );
  } else if (!personas || personas.length === 0) {
    content = (
      <EmptyState
        title={t('audienceFlowWidget.personaList.noPersonasFound')}
        description={t('audienceFlowWidget.personaList.createPersonasInTheKnowledge')}
        data-testid="persona-list-empty-state"
        height="100%"
      />
    );
  } else {
    content = (
      <div className={gridClasses}>
        {personas.map((persona) => (
          <PersonaListBlock
            key={persona.id}
            persona={persona}
            onPersonaClick={onPersonaClick}
            onStartConversation={onStartConversation}
            isCurrentPersonaSelected={currentAgentId === audienceFlowPersonaPrefix + persona.id}
            isStartConversationDisabled={isStartConversationDisabled}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full px-10 pb-10 gap-5">
      <div className="mt-8 mb-8">
        <h2 className="text-heading-xl text-foreground">
          {t('audienceFlowWidget.personaList.whoWouldYouLikeTo')}
        </h2>
      </div>
      {content}
    </div>
  );
};
